
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { StoryService, StoryData } from '../../services/storyService';
import EnhancedPrintLayoutDesigner from './EnhancedPrintLayoutDesigner';

interface LibrarySectionProps {
  currentUser: User;
}

// Helper function to safely convert Firebase Timestamps to Dates
const timestampToDate = (timestamp: any): Date => {
  if (!timestamp) return new Date(0);
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  return new Date(timestamp);
};

// Helper function to safely format dates
const formatTimestamp = (timestamp: any): string => {
  try {
    return timestampToDate(timestamp).toLocaleDateString();
  } catch {
    return 'Unknown date';
  }
};

const LibrarySection: React.FC<LibrarySectionProps> = ({ currentUser }) => {
  const [stories, setStories] = useState<StoryData[]>([]);
  const [filter, setFilter] = useState<'all' | 'graphic-novel' | 'regular-book' | 'picture-book'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'title' | 'type'>('recent');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedStory, setSelectedStory] = useState<StoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  };

  // Load user's stories from Firebase
  useEffect(() => {
    const loadUserStories = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const userStories = await StoryService.getUserStories(currentUser.uid);
        setStories(userStories);
      } catch (error) {
        console.error('Error loading stories:', error);
        setError('Failed to load your stories. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadUserStories();
  }, [currentUser]);

  const getTypeIcon = (type: StoryData['type']) => {
    switch (type) {
      case 'graphic-novel': return '📚';
      case 'picture-book': return '🎨';
      case 'regular-book': return '📖';
      default: return '📄';
    }
  };

  const getTypeLabel = (type: StoryData['type']) => {
    switch (type) {
      case 'graphic-novel': return 'Graphic Novel';
      case 'picture-book': return 'Picture Book';
      case 'regular-book': return 'Chapter Book';
      default: return 'Story';
    }
  };

  const getTypeColor = (type: StoryData['type']) => {
    switch (type) {
      case 'graphic-novel': return theme.primary;
      case 'picture-book': return theme.pink;
      case 'regular-book': return theme.blue;
      default: return theme.secondary;
    }
  };

  const handleEditStory = (story: StoryData) => {
    // Navigate to appropriate editor with story data
    switch (story.type) {
      case 'graphic-novel':
        navigate('/graphic-novel-editor', { state: { storyId: story.id } });
        break;
      case 'picture-book':
        navigate('/picture-book-editor', { state: { storyId: story.id } });
        break;
      case 'regular-book':
        navigate('/regular-book-editor', { state: { storyId: story.id } });
        break;
    }
  };

  const handlePrintStory = (story: StoryData) => {
    setSelectedStory(story);
    setShowPrintModal(true);
  };

  const handleShareStory = async (story: StoryData) => {
    try {
      // Update story to be published/shared
      if (story.id) {
        await StoryService.updateStory(currentUser.uid, story.id, { isPublished: true });
        // Refresh stories to show updated status
        const updatedStories = await StoryService.getUserStories(currentUser.uid);
        setStories(updatedStories);
      }
      console.log('Story shared:', story.title);
    } catch (error) {
      console.error('Error sharing story:', error);
    }
  };

  const handleDeleteStory = async (storyId: string) => {
    if (!storyId) {
      console.error('Cannot delete story: storyId is undefined');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this story? This action cannot be undone.')) {
      try {
        await StoryService.deleteStory(currentUser.uid, storyId);
        // Remove from local state
        setStories(stories.filter(s => s.id !== storyId));
      } catch (error) {
        console.error('Error deleting story:', error);
        setError('Failed to delete story. Please try again.');
      }
    }
  };

  const handleCreateNew = (type: StoryData['type']) => {
    switch (type) {
      case 'graphic-novel':
        navigate('/graphic-novel-editor');
        break;
      case 'picture-book':
        navigate('/picture-book-editor');
        break;
      case 'regular-book':
        navigate('/regular-book-editor');
        break;
    }
  };

  const filteredAndSortedStories = stories
    .filter(story => {
      if (filter !== 'all' && story.type !== filter) return false;
      if (searchTerm && !story.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          const aTime = timestampToDate(a.lastModified);
          const bTime = timestampToDate(b.lastModified);
          return bTime.getTime() - aTime.getTime();
        case 'title':
          return a.title.localeCompare(b.title);
        case 'type':
          return a.type.localeCompare(b.type);
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '60px',
        textAlign: 'center',
        boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
        border: `4px solid ${theme.primary}`
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: `4px solid ${theme.primary}30`,
          borderTop: `4px solid ${theme.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px auto'
        }} />
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: theme.primary,
          margin: 0
        }}>
          Loading your stories...
        </h2>
      </div>
    );
  }

  return (
    <>
      {/* Create New Stories Section */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '30px',
        marginBottom: '30px',
        boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
        border: `4px solid ${theme.success}`
      }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '800',
          margin: '0 0 20px 0',
          color: theme.success
        }}>
          ✨ Create New Story
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px'
        }}>
          <button
            onClick={() => handleCreateNew('graphic-novel')}
            style={{
              padding: '25px',
              borderRadius: '15px',
              border: `3px solid ${theme.primary}30`,
              background: `${theme.primary}05`,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.background = `${theme.primary}15`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = `${theme.primary}30`;
              e.currentTarget.style.background = `${theme.primary}05`;
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '15px' }}>📚</div>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: theme.primary,
              marginBottom: '8px'
            }}>
              Graphic Novel
            </div>
            <div style={{
              fontSize: '12px',
              color: '#666',
              lineHeight: '1.4'
            }}>
              Create comics with panels, speech bubbles, and visual storytelling
            </div>
          </button>

          <button
            onClick={() => handleCreateNew('picture-book')}
            style={{
              padding: '25px',
              borderRadius: '15px',
              border: `3px solid ${theme.pink}30`,
              background: `${theme.pink}05`,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.pink;
              e.currentTarget.style.background = `${theme.pink}15`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = `${theme.pink}30`;
              e.currentTarget.style.background = `${theme.pink}05`;
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '15px' }}>🎨</div>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: theme.pink,
              marginBottom: '8px'
            }}>
              Picture Book
            </div>
            <div style={{
              fontSize: '12px',
              color: '#666',
              lineHeight: '1.4'
            }}>
              Combine beautiful illustrations with simple, engaging text
            </div>
          </button>

          <button
            onClick={() => handleCreateNew('regular-book')}
            style={{
              padding: '25px',
              borderRadius: '15px',
              border: `3px solid ${theme.blue}30`,
              background: `${theme.blue}05`,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.blue;
              e.currentTarget.style.background = `${theme.blue}15`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = `${theme.blue}30`;
              e.currentTarget.style.background = `${theme.blue}05`;
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '15px' }}>📖</div>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: theme.blue,
              marginBottom: '8px'
            }}>
              Chapter Book
            </div>
            <div style={{
              fontSize: '12px',
              color: '#666',
              lineHeight: '1.4'
            }}>
              Write longer stories with chapters, characters, and detailed plots
            </div>
          </button>
        </div>
      </div>

      {/* Stories Library Section */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '30px',
        boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
        border: `4px solid ${theme.primary}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '800',
            margin: 0,
            color: theme.primary
          }}>
            📚 My Stories ({stories.length})
          </h2>
        </div>

        {/* Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px',
          marginBottom: '30px',
          padding: '20px',
          background: '#f8f9fa',
          borderRadius: '12px',
          border: '2px solid #e9ecef'
        }}>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search your stories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '10px',
                fontSize: '14px',
                width: '250px',
                outline: 'none'
              }}
            />
            
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '10px',
                fontSize: '14px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Stories</option>
              <option value="graphic-novel">Graphic Novels</option>
              <option value="picture-book">Picture Books</option>
              <option value="regular-book">Chapter Books</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '10px',
                fontSize: '14px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="recent">Most Recent</option>
              <option value="title">By Title</option>
              <option value="type">By Type</option>
            </select>
          </div>

          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#666'
          }}>
            {filteredAndSortedStories.length} {filteredAndSortedStories.length === 1 ? 'story' : 'stories'}
          </div>
        </div>

        {/* Stories Grid */}
        {filteredAndSortedStories.length === 0 ? (
          <div style={{
            padding: '60px',
            textAlign: 'center',
            background: '#f8f9fa',
            borderRadius: '15px',
            border: '2px solid #e9ecef'
          }}>
            <div style={{ fontSize: '72px', marginBottom: '20px' }}>📚</div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: theme.accent,
              margin: '0 0 15px 0'
            }}>
              {searchTerm || filter !== 'all' ? 'No matching stories found' : 'Your story collection is empty'}
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#666',
              margin: '0 0 30px 0'
            }}>
              {searchTerm || filter !== 'all' 
                ? 'Try adjusting your search or filter settings'
                : 'Create your first story using one of the options above!'
              }
            </p>
            {(searchTerm || filter !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilter('all');
                }}
                style={{
                  background: theme.accent,
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '700'
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '25px'
          }}>
            {filteredAndSortedStories.map(story => (
              <div
                key={story.id}
                style={{
                  background: 'white',
                  borderRadius: '15px',
                  padding: '20px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  border: `3px solid ${getTypeColor(story.type)}20`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = `0 10px 30px ${getTypeColor(story.type)}30`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
                }}
              >
                {/* Story Preview */}
                <div style={{
                  width: '100%',
                  height: '120px',
                  background: `${getTypeColor(story.type)}10`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '15px',
                  border: `2px solid ${getTypeColor(story.type)}20`
                }}>
                  <div style={{
                    fontSize: '36px',
                    opacity: 0.7
                  }}>
                    {getTypeIcon(story.type)}
                  </div>
                </div>

                {/* Story Info */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <h4 style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      margin: 0,
                      color: '#333',
                      lineHeight: '1.3'
                    }}>
                      {story.title}
                    </h4>
                    <div style={{
                      background: getTypeColor(story.type),
                      color: 'white',
                      padding: '3px 6px',
                      borderRadius: '4px',
                      fontSize: '9px',
                      fontWeight: '700',
                      whiteSpace: 'nowrap'
                    }}>
                      {getTypeLabel(story.type)}
                    </div>
                  </div>
                  
                  {story.description && (
                    <p style={{
                      fontSize: '12px',
                      color: '#666',
                      margin: '0 0 8px 0',
                      lineHeight: '1.4'
                    }}>
                      {story.description}
                    </p>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '11px',
                    color: '#999',
                    fontWeight: '600'
                  }}>
                    <span>{story.pageCount} pages</span>
                    <span>{formatTimestamp(story.lastModified)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr auto',
                  gap: '6px'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditStory(story);
                    }}
                    style={{
                      background: getTypeColor(story.type),
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: '700'
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrintStory(story);
                    }}
                    style={{
                      background: theme.success,
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: '700'
                    }}
                  >
                    🖨️ Print
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShareStory(story);
                    }}
                    style={{
                      background: theme.blue,
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: '700'
                    }}
                  >
                    🌟 Share
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (story.id) {
                        handleDeleteStory(story.id);
                      }
                    }}
                    style={{
                      background: '#ffebee',
                      color: '#d32f2f',
                      border: '2px solid #ffcdd2',
                      padding: '6px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '11px'
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Print Modal with Multi-Step Designer */}
      {showPrintModal && selectedStory && (
        <EnhancedPrintLayoutDesigner
          story={selectedStory}
          onClose={() => setShowPrintModal(false)}
          onExportPDF={(data) => {
            console.log('Exporting PDF:', data);
            // Here you would integrate with PDF generation service
            setShowPrintModal(false);
          }}
        />
      )}
    </>
  );
};

export default LibrarySection;